import React, { useEffect, useState } from "react";
import {
	Table,
	Button,
	Row,
	Col,
	Tag,
	Card,
	Form,
	Select,
	Input,
	notification,
	Modal,
} from "antd";
import { SolutionOutlined } from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import equipmentRequestService from "../../services/equipmentRequest";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Excel } from "antd-table-saveas-excel";
import { useMediaQuery } from "react-responsive";
import TableActionBtnsComponent from "../../components/tableActionsBtns";
import LocationComponent from "../../components/location";
import employeeService from "../../services/user";
import sapService from "../../services/sap";

const { confirm } = Modal;

const GetEquipmentRequest = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const { currentUser } = useSelector((state) => state.user);
	const [searchForm] = Form.useForm();
	const [formVisible, setFormVisible] = useState(false);
	const [formId] = useState(7);
	const [sortConfig, setSortConfig] = useState({ field: "", order: "" });

	// Build a user-scoped storage key so state never bleeds across accounts
	const sapLocalKey = currentUser?.user_id ? `sapLocal_${currentUser.user_id}` : null;
	const SAP_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

	const allowedPermissions = currentUser?.["roles.permission"]?.map(
		(permission) => permission.name
	);

	const hasPermission = (permission) =>
	allowedPermissions?.includes(permission);

	// Load saved filters, pagination, and sorting
	useEffect(() => {
		if (!sapLocalKey) return;
		try {
			const saved = localStorage.getItem(sapLocalKey);
			if (saved) {
				const parsed = JSON.parse(saved);
				// Enforce 24-hour expiry
				if (parsed.savedAt && Date.now() - parsed.savedAt > SAP_TTL_MS) {
					localStorage.removeItem(sapLocalKey);
					return;
				}
				searchForm.setFieldsValue(parsed.filters || {});
				setCurrentPage(parsed.page || 1);
				setItemsPerPage(parsed.pageSize || 10);
				setSortConfig(parsed.sort || { field: "", order: "" });
				onSearch(parsed.filters || {}, parsed.page || 1, parsed.pageSize || 10, parsed.sort || { field: "", order: "" });
				setFormVisible(true);
			}
		} catch {
			if (sapLocalKey) localStorage.removeItem(sapLocalKey);
		}
	}, [sapLocalKey]);


	const toggleFormVisibility = () => setFormVisible(!formVisible);

	const saveStateToLocal = (filters, page, pageSize, sort) => {
		if (!sapLocalKey) return;
		try {
			localStorage.setItem(
				sapLocalKey,
				JSON.stringify({ filters, page, pageSize, sort, savedAt: Date.now() })
			);
		} catch (e) {
			console.warn("Could not save SAP state:", e);
		}
	};

	const onSearch = async (values, page = 1, pageSize = itemsPerPage, sort = sortConfig) => {
		setLoading(true);
		saveStateToLocal(values, page, pageSize, sort);
		try {
			const response = await sapService.searchSap({ ...values, page, perPage: pageSize, sortBy: sort.field, sortOrder: sort.order });
			setData(response.data);
			// setCurrentPage(response.current_page);
			setTotalDataCount(response.total);
			setTotalDataCount(response.meta.total);
			setItemsPerPage(response.per_page);
		} catch (error) {
			console.error("Error while searching SAP Forms:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleClearFilters = () => {
		searchForm.resetFields();
		setCurrentPage(1);
		setSortConfig({ field: "", order: "" });

		if (sapLocalKey) {
			localStorage.removeItem(sapLocalKey);
		}

		getSap(1, itemsPerPage, "", "");
	};

	const handleDelete = (record) => {
		confirm({
			title: "Are you sure you want to delete this Capital Request Form?",
			content: "This action cannot be undone.",
			okText: "Yes",
			okType: "danger",
			cancelText: "No",
			onOk: async () => {
				try {
					const response = await equipmentRequestService.deleteCrf(record.id);
					if (response.success) {
						notification.success({
							message: "Capital Request Form Deleted",
							description: response.message,
						});
						await getSap(currentPage, itemsPerPage, sortConfig.field, sortConfig.order);
					}
				} catch (error) {
					notification.error({
						message: "Error",
						description: error.response?.data?.message || error.message,
					});
				}
			},
			onCancel() {
				console.log("Delete cancelled");
			},
		});
	};

	const columns = [
		{
			title: "Sequence No",
			dataIndex: "sequence_no",
			key: "sequence_no",
			fixed: useMediaQuery({ minWidth: 768 }) ? "left" : null,
			sorter: true,
		},
		{
			title: "Request Title",
			dataIndex: "request_title",
			key: "request_title",
			sorter: true,
			render: (text) => (text.length > 40 ? `${text.slice(0, 40)}...` : text),
		},
		{
			title: "For Employee",
			key: "for_employee",
			sorter: true,
			render: (_, record) => record?.data?.for_employee || record?.for_employee || "-",
		},
		{
			title: "Location",
			dataIndex: ["location", "name"],
			key: "location",
			sorter: true,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (status) => (
				<Tag
					color={
						(status === "Pending" && "orange") ||
						(status === "Approved" && "green") ||
						(status === "Return" && "red")
					}
				>
					{status}
				</Tag>
			),
			sorter: true,
		},
		{
			title: "Created At",
			dataIndex: "created_at",
			key: "created_at",
			sorter: true,
		},
		{
			title: "Actions",
			dataIndex: "Actions",
			key: "Actions",
			width: 80,
			fixed: useMediaQuery({ minWidth: 768 }) ? "right" : null,
			render: (text, record) => (
				<TableActionBtnsComponent
					record={record}
					handleDelete={handleDelete}
					currentUser={currentUser}
					formId={formId}
				/>
			),
		},
	];

	const handleExport = async () => {
		try {
			const response = await sapService.getSap(1, 1000);
			const allData = response.data;
			const excelColumns = columns
				.filter((column) => column.key !== "Actions")
				.map(({ render, ...col }) => col);

			const excel = new Excel();
			excel
				.addSheet("SAP Access Forms")
				.addColumns(excelColumns)
				.addDataSource(allData, { str2Percent: true })
				.saveAs("SAP_Access_Forms.xlsx");
		} catch (error) {
			console.error("Error exporting all data:", error);
		}
	};

	const getSap = async (page = 1, pageSize = 15, sortBy = "", sortOrder = "") => {
		setLoading(true);
		try {
			const response = await sapService.getSap(page, pageSize, sortBy, sortOrder);
			setData(response.data);
			setCurrentPage(response.meta.current_page);
			setTotalDataCount(response.meta.total);
			setItemsPerPage(response.meta.per_page);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!sapLocalKey || !localStorage.getItem(sapLocalKey)) getSap();
	}, [sapLocalKey]);

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header
						icon={<SolutionOutlined />}
						title="SAP Access Form"
						right={
							<>

								{hasPermission("OwnWorkflow-view") && (
									<Link to={`/own-workflow?form_id=7`}>
										<Button className='mr-10'>My Workflows</Button>
									</Link>
								)}
								<Button onClick={handleExport} className="mr-10">
									Export
								</Button>
								<Link to="/sap-access-form/add">
									<Button className="btn-blue" type="primary">
										Add SAP Access Form
									</Button>
								</Link>
							</>
						}
					/>
				</Col>
			</Row>

			<Card>
				<Button onClick={toggleFormVisibility}>
					{formVisible ? "Hide Search Form" : "Show Search Form"}
				</Button>

				{formVisible && (
					<Form form={searchForm} onFinish={onSearch} layout="vertical">
						<Card className="pt-6" style={{ paddingBottom: "0 !important" }}>
							<Row gutter={[12, 12]}>
								{[
									{ key: "sequence_no", placeholder: "Sequence No" },
									{ key: "request_title", placeholder: "Request Title" },
								].map(({ key, placeholder }) => (
									<Col key={key} lg={6} md={12} sm={24} xs={24}>
										<Form.Item name={key}>
											<Input size="large" placeholder={placeholder} allowClear />
										</Form.Item>
									</Col>
								))}

								{/* Employee Filter */}
								<Col lg={6} md={12} sm={24} xs={24}>
									<Form.Item name="for_employee">
										<Input size="large" placeholder="For Employee" allowClear />
									</Form.Item>
								</Col>

								<Col lg={6} md={12} sm={24} xs={24}>
									<Form.Item name="location_id">
										<LocationComponent all={true} />
									</Form.Item>
								</Col>

								{/* Status Filter */}
								<Col lg={6} md={12} sm={24} xs={24}>
									<Form.Item name="status">
										<Select
											size="large"
											placeholder="Select Status"
											allowClear
											options={[
												{ value: 'Pending', label: 'Pending' },
												{ value: 'Draft', label: 'Draft' },
												{ value: 'Approved', label: 'Approved / Disapproved' },
											]}
										/>
									</Form.Item>
								</Col>

								<Col span={24}>
									<div style={{ display: "flex", gap: "10px" }}>
										<Button block className="btn-blue" htmlType="submit" type="primary">
											Search
										</Button>
										<Button block onClick={handleClearFilters}>
											Clear
										</Button>
									</div>
								</Col>
							</Row>
						</Card>
					</Form>
				)}
			</Card>

			<Card>
				<Table
					columns={columns}
					dataSource={data.map((item) => ({ ...item, key: item.id }))}
					style={{ minHeight: "100vh" }}
					scroll={{ x: 1200 }}
					sticky
					loading={loading}
					pagination={{
						showSizeChanger: true,
						pageSizeOptions: ["10", "15", "25", "50", "100"],
						current: currentPage,
						total: totalDataCount,
						pageSize: itemsPerPage,
					}}
					onChange={(pagination, filters, sorter) => {
						const { order, field } = sorter;
						const sortOrder =
							order === "ascend" ? "asc" : order === "descend" ? "desc" : "";
						const sortObj = { field, order: sortOrder };
						setSortConfig(sortObj);
						setCurrentPage(pagination.current);
						setItemsPerPage(pagination.pageSize);
						saveStateToLocal(searchForm.getFieldsValue(), pagination.current, pagination.pageSize, sortObj);
						getSap(pagination.current, pagination.pageSize, field, sortOrder);
					}}
				/>
			</Card>
		</DefaultLayout>
	);
};

export default GetEquipmentRequest;